/**
 * hooks/useDynamicDocumentMeta.ts
 *
 * Dynamically updates the browser tab title and favicon.
 * Used by public-facing form pages (ClientCollect, SharedFormLanding) to
 * reflect the sender's branding in the browser chrome.
 *
 * Created Apr 2026.
 */

import { useEffect } from 'react';

interface UseDynamicDocumentMetaOptions {
  title?: string;
  faviconUrl?: string | null;
}

export function useDynamicDocumentMeta({ title, faviconUrl }: UseDynamicDocumentMetaOptions) {
  useEffect(() => {
    if (!title) return;
    const prev = document.title;
    document.title = title;
    return () => { document.title = prev; };
  }, [title]);

  useEffect(() => {
    if (!faviconUrl) return;

    const existingFavicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    const prevHref = existingFavicon?.href ?? null;

    let link = existingFavicon;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;

    return () => {
      if (prevHref && link) {
        link.href = prevHref;
      } else if (!prevHref && link) {
        link.remove();
      }
    };
  }, [faviconUrl]);
}
